// ---------------------------------------------------------------------------
// LES RÉGLAGES DES POINTS — ce que la section admin décide, et que le bot relit
//
// Toji, 14/09 : « dans la section admin, on mette toute la gestion au niveau
// des points, un peu comme ce qu'on a fait pour Roma, mais en simplifié. Il y a
// quand même le système des multiplicateurs, etc. […] on mettrait juste des
// multiplicateurs en fonction de certains éléments et de certains rôles. »
//
// Rien n'est figé dans le code : les chiffres du document de conception ne sont
// que les VALEURS PAR DÉFAUT, celles d'un serveur qui n'a rien réglé. Tout se
// change depuis l'écran d'administration du lecteur, qui écrit `access.json` —
// le même fichier que le bot relit à chaud, sans redémarrage (`reglages.js`).
//
// ⚠️ CE MODULE NE FAIT QUE NORMALISER, ET C'EST TOUT SON INTÉRÊT. Un réglage
// vient d'un formulaire : il arrive en texte, parfois vide, parfois absurde
// (un prix négatif, un multiplicateur à zéro qui annulerait tout gain). Aucun
// appelant ne doit avoir à s'en méfier. Ce qui sort d'ici est toujours complet
// et toujours sensé, quoi qu'on lui ait donné — un réglage illisible retombe
// sur son défaut au lieu de casser l'économie du serveur.
// ---------------------------------------------------------------------------

import { DAILY_DEFAUTS } from './daily.js';

/**
 * Les valeurs par défaut : celles de `docs/ECONOMIE-DE-SEANCE.md`.
 *
 * Un serveur qui n'a jamais ouvert l'écran d'administration tourne avec
 * exactement ces chiffres, et ils sont cohérents entre eux : trente minutes de
 * vocal paient un épisode.
 */
export const DEFAUTS = Object.freeze({
  actif: true,
  // ---- ce qui rapporte ----------------------------------------------------
  parMinuteVocale: 1,
  parMessage: 1,
  delaiEntreMessagesS: 60,
  minimumEnVocal: 2,
  plafond: 240,
  // ⚠️ UN PLAFOND PAR JOUR ET PAR SOURCE, distinct du plafond de SOLDE.
  //
  // Toji, 14/09 : « tu mets surtout que c'est 120 max par jour en points. Par
  // message, et la même chose en vocal, sans compter le multiplicateur. »
  //
  // Sans lui, quelqu'un qui laisse un micro ouvert toute la nuit gagne mille
  // points pendant que les autres en gagnent trente : le classement ne
  // récompense plus la présence mais l'endurance d'une machine. Le délai entre
  // messages protégeait déjà du spam écrit ; rien ne protégeait du vocal.
  //
  // ⚠️ ET IL SE COMPTE AVANT LE MULTIPLICATEUR. Sinon un rôle à ×2 réduirait de
  // moitié le temps qu'on peut passer à gagner — le bonus deviendrait une
  // punition, ce qui est exactement l'inverse de ce qu'il est.
  maxParJourVocal: 120,
  maxParJourMessages: 120,
  // ---- ce qui coûte -------------------------------------------------------
  // Deux façons de facturer, au choix de l'exploitant (Toji, 14/09 : « on peut
  // soit choisir pour un épisode, soit choisir une durée ») :
  //  · `episode` — on paie à la pièce, et un lot de plusieurs épisodes profite
  //    des paliers de volume ;
  //  · `duree`   — on achète du temps de lecture par tranches, et c'est la
  //    durée réelle de la soirée qui décide du nombre de tranches.
  mode: 'episode',
  prixEpisode: 30,
  prixFilm: 60,
  // Facturation à la durée : le prix d'une tranche, et ce qu'elle couvre.
  prixTranche: 30,
  minutesParTranche: 60,
  remiseBoosterPourcent: 50,
  // ---- la réclamation quotidienne -----------------------------------------
  // Les chiffres sont ceux de Roma (voir `daily.js`), et ils sont pris LÀ-BAS :
  // deux copies du même barème finiraient par diverger, et le montant réellement
  // crédité ne serait plus celui que l'écran d'administration annonce.
  //
  // ⚠️ UN INTERRUPTEUR À PART, PAS `actif`. `actif: false` coupe toute
  // l'économie — les séances redeviennent gratuites. Un serveur peut vouloir
  // garder les points du vocal et se passer de la daily (ou l'inverse) sans
  // rendre les épisodes gratuits pour autant.
  // ⚠️ ÉTEINTE PAR DÉFAUT (Toji, 14/09 : « tu peux m'enlever la daily ? »).
  // Le moteur reste entier et éprouvé — série, niveaux, script atomique : la
  // rallumer est un réglage, pas un développement. Ce qui est retiré, c'est
  // seulement ce que les gens voient.
  dailyActif: false,
  dailyBase: DAILY_DEFAUTS.base,
  dailyParJour: DAILY_DEFAUTS.parJour,
  dailyMaxBonus: DAILY_DEFAUTS.maxBonus,
  dailyMaxJours: DAILY_DEFAUTS.maxJours,
  // ---- les remises de volume ----------------------------------------------
  // Ce qui donne un sens à l'épargne (Toji : « on fait des discounts pour des
  // grandes sessions […] les gens peuvent l'économiser […] ils déclenchent tout
  // dans une soirée »). Vide par défaut : un serveur neuf facture au prix.
  paliers: [],
  // ---- les multiplicateurs par rôle ---------------------------------------
  // Vide par défaut : un serveur neuf ne favorise personne.
  multiplicateurs: [],
  // ---- LE CARNET ----------------------------------------------------------
  // Ce qu'on achète à l'avance : des UNITÉS de facturation, rangées dans un
  // carnet scellé sous le barème du jour de l'achat (`_agents/METHODOLOGIE-
  // POINTS.md`, §1). Rien de tout cela n'est branché tant que `carnetActif`
  // n'est pas mis à `true` : les huit clés qui suivent ne font, aujourd'hui,
  // que sortir d'ici avec des valeurs sensées.
  //
  // ⚠️ ÉTEINT PAR DÉFAUT, ET SEUL UN `true` EXPLICITE L'ALLUME — la même règle
  // que `dailyActif`, et pour la même raison : un `access.json` écrit avant que
  // le carnet existe ne doit pas ouvrir une boutique sur le serveur de
  // quelqu'un qui n'a rien demandé. `actif` fait l'inverse (il reste en marche
  // quand on ne dit rien) parce qu'un réglage oublié ne doit pas rendre les
  // épisodes gratuits ; ici, le réglage oublié ne doit rien vendre.
  carnetActif: false,
  // La NATURE vendue par le carnet. Une unité de carnet, c'est un épisode, ou
  // c'est un film — jamais les deux : l'empreinte du barème gelé la couvre, et
  // un carnet d'épisodes et un carnet de films sont deux carnets distincts
  // (§1.3). Sans elle, on retombe sur la faille F7, « le billet au porteur » :
  // acheter dix unités au tarif épisode puis lancer dix films, soit 600 points
  // de contenu pour 225 — et `prixFilm` devient un réglage mort.
  nature: 'episode',
  // Le prix d'UNE unité de carnet. `0` veut dire « suis la nature vendue » et
  // c'est le défaut : le prix reste alors exactement celui d'aujourd'hui
  // (`prixEpisode`, `prixFilm` ou `prixTranche` selon le mode et la nature),
  // et un exploitant qui change `prixEpisode` voit le carnet suivre.
  //
  // ⚠️ IL NE FAUT SURTOUT PAS QUE LE DÉFAUT SOIT `30`. Une copie figée du prix
  // de l'épisode diverge au premier `PUT` qui touche `prixEpisode` : l'écran
  // d'administration afficherait 50 et le carnet facturerait encore 30, sans
  // qu'une seule ligne ne le signale. Le `0` sentinelle n'a pas ce défaut, et
  // `prixUnitaireDe` est le seul endroit où il se résout.
  prixUnitaire: 0,
  // Combien d'unités un carnet peut contenir, par personne et par serveur.
  // C'est la parade à F13 : sans borne, on descend son solde à zéro en
  // achetant, on regagne le plafond journalier, on recommence — le plafond de
  // solde ne borne plus rien et le portefeuille devient illimité.
  // ⚠️ L'achat au-delà est REFUSÉ, jamais écrêté (§1.8) : écrêter, c'est
  // prendre l'argent de dix unités et n'en livrer que trois.
  carnetMax: 50,
  // Combien d'unités on peut acheter EN UNE FOIS. Borne de geste, pas de
  // stock : le carnet reste borné par `carnetMax`, celle-ci borne la rangée
  // de quantités de l'écran d'achat (`− 1 3 5 10 20 +`, §2) et ce qu'un
  // `custom_id` fabriqué à la main peut demander.
  //
  // Deux bornes plutôt qu'une parce qu'elles disent deux choses : `carnetMax`
  // est ce qu'on a le droit de POSSÉDER, `achatMax` ce qu'on a le droit
  // d'engager d'un seul clic. La seconde est ce qui rend F14 (la
  // thésaurisation la veille d'une hausse annoncée) lente au lieu
  // d'instantanée, sans interdire l'épargne qui est tout l'intérêt du système.
  achatMax: 20,
  // La reprise d'un carnet au tarif du jour (§3). Permise par défaut : c'est
  // le geste qui rend une baisse de remise supportable, et il ne peut jamais
  // coûter à personne — la reprise ne rend que la différence, et refuse quand
  // elle est nulle ou négative.
  repriseActive: true,
  // ---- le plafond de solde, et ce que le niveau lui ajoute ----------------
  // ⚠️ `plafond` (plus haut) est le plafond de SOLDE — à ne pas confondre avec
  // `maxParJourVocal`/`maxParJourMessages`, qui sont des plafonds de GAIN par
  // jour. Les deux clés qui suivent ne font que le rendre progressif.
  //
  // Plafond personnel = min(plafondMax, plafond + plafondParNiveau × (niveau − 1)).
  // À `0`, la formule rend `plafond` pour tout le monde, exactement comme
  // aujourd'hui : c'est le défaut, et il est strictement neutre.
  //
  // C'est la seule conséquence économique du niveau (§5.3), et c'est voulu :
  // une remise adossée au niveau rouvrirait F2 par une autre porte, puisqu'il
  // redeviendrait rentable de fabriquer du cumul pour payer moins cher.
  plafondParNiveau: 0,
  // La borne dure du plafond personnel. Elle ne peut jamais faire BAISSER le
  // plafond de base — voir `plafondPersonnel`.
  plafondMax: 5_000,
  // ---- LES GROUPES (phase 4, 22/10) ---------------------------------------
  // Proposition validée (vault : Proposition-Points-Activite-2026-10-22) :
  // rejoindre un groupe en cours est gratuit et rapporte, OUVRIR un groupe
  // coûte `base × charge × qualité × heure` (economie.js `prixDuGroupe`).
  // ⚠️ ÉTEINT PAR DÉFAUT, seul un `true` explicite l'allume (même règle que le
  // carnet) : un `access.json` d'avant ne doit pas se mettre à faire payer la
  // lecture d'un serveur qui n'a rien décidé. Toji l'allume dans l'écran.
  groupesActif: false,
  // Groupes simultanés ; au-delà : « Rejoindre X » ou la file d'attente.
  plafondGroupes: 5,
  // ×2 au plafond (charge = 1 jusqu'à la moitié du plafond).
  chargeMaxPourcent: 200,
  coefLegerPourcent: 70,
  coefHautPourcent: 150,
  // Heures de Paris, plages [début, fin[ ; une plage peut passer minuit.
  creusesDebut: 2,
  creusesFin: 17,
  coefCreusesPourcent: 70,
  picDebut: 20,
  picFin: 23,
  coefPicPourcent: 120,
  // Rejoindre : + bonus par épisode vu en entier, plafonné par jour.
  bonusGroupe: 5,
  bonusGroupeMaxJour: 30,
  remiseSeriePourcent: 30,
  remiseSuivantPourcent: 20,
  remiseCachePourcent: 20,
  // Jamais sous 30 % du prix de référence, toutes remises cumulées.
  plancherPourcent: 30,
  // Sous ce prix, aucune confirmation (annulable 10 min).
  confirmerDes: 50,
  // Anti-abus du bonus : ancienneté sur le serveur et du compte Discord.
  ancienneteMembreJours: 7,
  ancienneteCompteJours: 30,
  // Fidélité : le plus haut palier atteint (épisodes vus) gagne.
  fidelite: [
    { vus: 10, remisePourcent: 5 },
    { vus: 50, remisePourcent: 10 },
    { vus: 100, remisePourcent: 15 },
  ],
  // Promos datées : `{ serie, remisePourcent, fin: 'AAAA-MM-JJ', titre }`.
  promos: [],
});

/** Les clés des groupes : seul le PROPRIÉTAIRE les change (server.js, PUT /api/admin/points). */
export const CLES_GROUPES = Object.freeze([
  'groupesActif', 'plafondGroupes', 'chargeMaxPourcent', 'coefLegerPourcent', 'coefHautPourcent',
  'creusesDebut', 'creusesFin', 'coefCreusesPourcent', 'picDebut', 'picFin', 'coefPicPourcent',
  'bonusGroupe', 'bonusGroupeMaxJour', 'remiseSeriePourcent', 'remiseSuivantPourcent',
  'remiseCachePourcent', 'plancherPourcent', 'confirmerDes', 'ancienneteMembreJours',
  'ancienneteCompteJours', 'fidelite', 'promos',
]);

/** Les bornes : au-delà, ce n'est plus un réglage, c'est une faute de frappe. */
const BORNES = {
  parMinuteVocale: [0, 100],
  parMessage: [0, 100],
  delaiEntreMessagesS: [1, 3600],
  maxParJourVocal: [0, 100_000],
  maxParJourMessages: [0, 100_000],
  minimumEnVocal: [1, 99],
  plafond: [1, 1_000_000],
  prixEpisode: [0, 100_000],
  prixFilm: [0, 100_000],
  prixTranche: [0, 100_000],
  minutesParTranche: [1, 1440],
  remiseBoosterPourcent: [0, 100],
  // La daily peut valoir zéro (un serveur qui ne veut que le bonus de série,
  // ou que la base) — mais jamais moins, et jamais plus que le plafond d'un
  // solde : une daily au-dessus du plafond serait un bouton « remplir ».
  dailyBase: [0, 1_000_000],
  dailyParJour: [0, 1_000_000],
  dailyMaxBonus: [0, 1_000_000],
  // ⚠️ AU MOINS UN JOUR. À zéro, la série retenue serait nulle et le bonus
  // disparaîtrait pour tout le monde — ce qui se dit `dailyParJour: 0`, pas
  // « série de zéro jour ». Un an suffit largement comme borne haute.
  dailyMaxJours: [1, 365],
  // ---- le carnet ----------------------------------------------------------
  // `0` est la sentinelle « suis la nature vendue » : elle doit rester
  // atteignable, donc la borne basse est zéro et non un.
  prixUnitaire: [0, 100_000],
  // ⚠️ AU MOINS UNE UNITÉ. À zéro, `carnetActif: true` vendrait des carnets que
  // rien ne peut remplir : chaque achat serait refusé pour « carnet plein »,
  // ce qui est la panne la plus difficile à relier à son réglage.
  // 500 en haut : au-delà, la borne ne borne plus rien face à `plafond`.
  carnetMax: [1, 500],
  achatMax: [1, 500],
  // Zéro est le défaut et veut dire « le niveau n'ajoute rien ». 1 000 points
  // de plafond par niveau suffisent à toutes les courbes plausibles.
  plafondParNiveau: [0, 1_000],
  // Les mêmes bornes que `plafond` : c'est le même genre de nombre, et deux
  // grilles différentes pour un plafond et son maximum finiraient par se
  // croiser.
  plafondMax: [1, 1_000_000],
  // ---- les groupes ---------------------------------------------------------
  plafondGroupes: [1, 100],
  chargeMaxPourcent: [100, 500],
  coefLegerPourcent: [10, 100],
  coefHautPourcent: [100, 500],
  creusesDebut: [0, 23],
  creusesFin: [0, 23],
  coefCreusesPourcent: [10, 100],
  picDebut: [0, 23],
  picFin: [0, 23],
  coefPicPourcent: [100, 500],
  bonusGroupe: [0, 1_000],
  bonusGroupeMaxJour: [0, 100_000],
  remiseSeriePourcent: [0, 90],
  remiseSuivantPourcent: [0, 90],
  remiseCachePourcent: [0, 90],
  plancherPourcent: [0, 100],
  confirmerDes: [0, 100_000],
  ancienneteMembreJours: [0, 365],
  ancienneteCompteJours: [0, 3650],
};

/** Un palier de fidélité `{ vus, remisePourcent }`, ou `null`. */
function normaliserFidelite(brut) {
  const vus = Math.floor(Number(brut?.vus));
  const remise = Math.round(Number(brut?.remisePourcent));
  if (!Number.isFinite(vus) || vus < 1 || !Number.isFinite(remise) || remise <= 0) return null;
  return { vus, remisePourcent: Math.min(90, remise) };
}

/** Une promo `{ serie, remisePourcent, fin, titre }`, ou `null` (date `AAAA-MM-JJ` exigée). */
function normaliserPromo(brut) {
  const serie = String(brut?.serie ?? '').trim();
  const fin = String(brut?.fin ?? '').trim();
  const remise = Math.round(Number(brut?.remisePourcent));
  if (!/^[\w:.-]{1,64}$/.test(serie) || !/^\d{4}-\d{2}-\d{2}$/.test(fin)) return null;
  if (!Number.isFinite(remise) || remise <= 0) return null;
  return { serie, remisePourcent: Math.min(90, remise), fin, titre: String(brut?.titre ?? '').trim().slice(0, 120) };
}

/**
 * Les bornes des CONDITIONS DE VENTE gelées avec le barème (`participants`,
 * `boosters`).
 *
 * Elles ne viennent pas d'`access.json` : ce ne sont pas des réglages, c'est
 * l'état du salon à l'instant de la vente. Les borner ne sert donc qu'à une
 * seule chose — qu'aucun `Infinity`, aucun `NaN` et aucune fraction de
 * personne n'entre dans un champ dont dépend une empreinte (F16 : toute donnée
 * qui touche à l'argent est un entier fini).
 *
 * ⚠️ LA BORNE HAUTE EST VOLONTAIREMENT HORS D'ATTEINTE D'UNE VRAIE SÉANCE.
 * Écrêter le nombre de participants ferait MONTER la part boostée
 * (`boosters / participants`), donc la remise : un garde-fou qui fait un cadeau
 * n'est plus un garde-fou. On préfère une borne que personne n'atteint jamais.
 */
const PARTICIPANTS_BORNES = [1, 100_000];

/** Les deux façons de facturer. Toute autre valeur retombe sur `episode`. */
export const MODES = Object.freeze(['episode', 'duree']);

/**
 * Les natures qu'un carnet peut vendre. Toute autre valeur retombe sur
 * `episode`.
 *
 * ⚠️ LA MÊME LISTE QUE CELLE QUE LE RESTE DU BOT CONNAÎT DÉJÀ — `commandes.js`
 * et `donnees-membre.js` écrivent tous les deux `nature === 'film' ? 'film' :
 * 'episode'`, et `economie.js` ne tarife que ces deux-là (`PRIX`). Une
 * troisième nature ici serait vendue par le carnet et facturée nulle part.
 */
export const NATURES = Object.freeze(['episode', 'film']);

/** Au plus dix paliers : au-delà, un barème ne se lit plus. */
export const PALIERS_MAX = 10;

/** Le multiplicateur le plus fort accepté. Au-delà, l'économie n'a plus de sens. */
export const MULTIPLICATEUR_MAX = 10;

function entier(brut, defaut, [min, max]) {
  const n = Math.round(Number(brut));
  if (!Number.isFinite(n)) return defaut;
  return Math.min(max, Math.max(min, n));
}

/**
 * Un multiplicateur : `{ role, facteur, nom? }`.
 *
 * Le facteur est un décimal (1,5 ×), pas un entier — c'est ce qu'on veut pour
 * « un peu plus » sans doubler. Borné entre 0 et 10 : un facteur négatif
 * retirerait des points à quelqu'un qui vient de donner du temps, ce qui n'est
 * pas un réglage mais une punition, et rien ici ne punit.
 */
export function normaliserMultiplicateur(brut) {
  const role = String(brut?.role ?? brut?.roleId ?? '').trim();
  if (!role) return null;
  const f = Number(brut?.facteur ?? brut?.multiplicateur);
  if (!Number.isFinite(f) || f < 0) return null;
  return {
    role,
    facteur: Math.min(MULTIPLICATEUR_MAX, Math.round(f * 100) / 100),
    nom: String(brut?.nom ?? '').trim(),
  };
}

/**
 * Un palier de volume : `{ quantite, remisePourcent }`.
 *
 * Le seuil est une quantité (« à partir de 5 épisodes »), la remise un
 * pourcentage borné à 100 — au-delà on offrirait, et offrir se dit `actif:
 * false`, pas « remise 150 % ».
 */
export function normaliserPalier(brut) {
  const quantite = Math.floor(Number(brut?.quantite ?? brut?.seuil));
  const remise = Number(brut?.remisePourcent ?? brut?.remise);
  if (!Number.isFinite(quantite) || quantite < 1) return null;
  if (!Number.isFinite(remise) || remise <= 0) return null;
  return { quantite, remisePourcent: Math.min(100, Math.round(remise)) };
}

/**
 * La grille de paliers, triée par seuil croissant et dédoublonnée.
 *
 * ⚠️ ELLE EST AUSSI RENDUE MONOTONE : un palier plus gros ne peut pas offrir
 * MOINS qu'un palier plus petit. Sans ça, « 3 → 20 % » et « 10 → 5 % » saisis
 * dans cet ordre feraient qu'acheter dix épisodes coûterait plus cher qu'en
 * acheter trois — une remise qui punit le gros acheteur, exactement l'inverse
 * de ce qu'on voulait. On relève le palier fautif au lieu de le refuser, parce
 * qu'un formulaire mal rempli ne doit pas casser l'économie d'un serveur.
 */
export function normaliserPaliers(brut) {
  const vus = new Map();
  for (const p of Array.isArray(brut) ? brut : []) {
    const n = normaliserPalier(p);
    if (!n) continue;
    // Deux lignes pour le même seuil : la plus généreuse gagne.
    const deja = vus.get(n.quantite);
    if (!deja || n.remisePourcent > deja.remisePourcent) vus.set(n.quantite, n);
  }
  const tries = [...vus.values()].sort((a, b) => a.quantite - b.quantite).slice(0, PALIERS_MAX);
  let plancher = 0;
  for (const p of tries) {
    if (p.remisePourcent < plancher) p.remisePourcent = plancher;
    plancher = p.remisePourcent;
  }
  return tries;
}

/**
 * Les réglages complets, à partir de ce que porte `access.json`.
 *
 * Ne lève jamais. Un fichier absent, une section absente, une section remplie
 * de n'importe quoi : on rend les défauts. Le bot doit servir des séances sur
 * un serveur qui vient d'être installé.
 */
export function lirePoints(brut) {
  const src = brut && typeof brut === 'object' ? brut : {};
  const out = { ...DEFAUTS };

  for (const [cle, bornes] of Object.entries(BORNES)) {
    out[cle] = entier(src[cle], DEFAUTS[cle], bornes);
  }
  // `actif: false` coupe toute l'économie : les séances redeviennent libres.
  // Seul un `false` explicite coupe — une valeur absente laisse le système en
  // marche, parce qu'un réglage oublié ne doit pas ouvrir les vannes.
  out.actif = src.actif !== false;
  // Même règle pour la daily : seul un `false` explicite l'éteint. Une clé
  // absente (un `access.json` écrit avant que la daily existe) la laisse en
  // marche, plutôt que de la faire disparaître d'un serveur qui l'utilisait.
  // ⚠️ L INVERSE DE `actif` : il faut le dire pour l ALLUMER.
  //
  // `actif` reste en marche quand on ne dit rien, parce qu un reglage oublie ne
  // doit pas rendre les episodes gratuits. La daily est l oppose : Toji l a fait
  // retirer, et un fichier de reglages qui ne la mentionne pas ne doit pas la
  // faire reapparaitre. Seul un `true` explicite la rallume.
  out.dailyActif = src.dailyActif === true;
  // ⚠️ MÊME RÈGLE QUE LA DAILY, PAS CELLE D'`actif` : seul un `true` explicite
  // ouvre la boutique. Un `access.json` écrit avant que le carnet existe ne
  // mentionne pas la clé — et il ne doit pas se réveiller en vendant.
  out.carnetActif = src.carnetActif === true;
  // ⚠️ ET ICI L'INVERSE, PARCE QUE LA REPRISE N'EST PAS UNE VENTE. Elle ne peut
  // rien coûter à personne (elle ne rend que la différence, et refuse quand
  // celle-ci est nulle) ; l'éteindre est une décision, pas un oubli. Seul un
  // `false` explicite la ferme.
  out.repriseActive = src.repriseActive !== false;
  out.mode = MODES.includes(String(src.mode)) ? String(src.mode) : DEFAUTS.mode;
  out.nature = NATURES.includes(String(src.nature)) ? String(src.nature) : DEFAUTS.nature;
  out.paliers = normaliserPaliers(src.paliers);
  out.groupesActif = src.groupesActif === true;
  // Absent → les paliers par défaut ; une liste (même vide) → ce qu'elle dit.
  out.fidelite = Array.isArray(src.fidelite)
    ? src.fidelite.map(normaliserFidelite).filter(Boolean).sort((a, b) => a.vus - b.vus).slice(0, PALIERS_MAX)
    : DEFAUTS.fidelite.map((p) => ({ ...p }));
  out.promos = (Array.isArray(src.promos) ? src.promos : []).map(normaliserPromo).filter(Boolean).slice(0, 50);

  const vus = new Set();
  out.multiplicateurs = (Array.isArray(src.multiplicateurs) ? src.multiplicateurs : [])
    .map(normaliserMultiplicateur)
    .filter((m) => {
      // Deux lignes pour le même rôle : la première gagne. Les additionner ou
      // les multiplier ferait dépendre le gain de l'ordre du formulaire.
      if (!m || vus.has(m.role)) return false;
      vus.add(m.role);
      return true;
    });

  return out;
}

/**
 * Le facteur qui s'applique à quelqu'un, d'après ses rôles.
 *
 * ⚠️ ON PREND LE PLUS FORT, ON NE MULTIPLIE PAS ENTRE EUX. Trois rôles à 2 ×
 * donneraient 8 × si on les composait — et personne ne l'aurait voulu en
 * remplissant trois lignes d'un formulaire. « Le meilleur de ce que tu as » est
 * la seule lecture qui reste prévisible quand les rôles s'accumulent.
 *
 * Rend `1` quand rien ne s'applique : le facteur neutre, jamais `0`.
 */
export function facteurDe(roles = [], multiplicateurs = []) {
  const miens = new Set([...roles].map(String));
  // ⚠️ ON PART DE ×1, ET LES RÔLES S'AJOUTENT — ils ne se remplacent plus.
  //
  // Toji, 14/09 : « le multiplicateur, en base, on est en ×1. Si on rajoute un
  // rôle avec 1.5, ça fait 2.5. C'est de l'ajout à chaque fois. »
  //
  // Avant, on gardait le MEILLEUR : deux rôles à ×1,5 et ×2 donnaient ×2, et le
  // premier ne servait à rien dès qu'on avait le second. Cumuler récompense le
  // fait d'avoir les deux — c'est ce qui rend un petit rôle encore utile à
  // quelqu'un qui en a déjà un gros.
  let total = 1;
  for (const m of multiplicateurs) {
    if (!m?.role || !miens.has(String(m.role))) continue;
    const ajout = Number(m.facteur);
    if (Number.isFinite(ajout) && ajout > 0) total += ajout;
  }
  // ⚠️ LE PLAFOND VAUT POUR LA SOMME, PAS POUR CHAQUE RÔLE. Sans lui, dix rôles
  // à ×2 feraient ×21 — et `MULTIPLICATEUR_MAX` ne protégerait plus de rien,
  // puisqu'il ne borne qu'une valeur déclarée à la fois.
  return Math.min(total, 1 + MULTIPLICATEUR_MAX);
}

/**
 * Applique un facteur à un gain, en entiers.
 *
 * Arrondi vers le BAS : un facteur de 1,5 sur un point donne un point, pas
 * deux. Arrondir vers le haut transformerait un multiplicateur décoratif en
 * doublement systématique sur les petits gains — qui sont justement les plus
 * fréquents, puisqu'on crédite à la minute.
 */
export function appliquerFacteur(points, facteur = 1) {
  const p = Number(points) || 0;
  const f = Number(facteur);
  if (p <= 0 || !Number.isFinite(f) || f <= 0) return 0;
  return Math.floor(p * f);
}

/**
 * Le barème de la daily, extrait des réglages, dans la forme que
 * `montantDuDaily` et `magasin.reclamerDaily` attendent.
 *
 * ⚠️ UNE SEULE TRADUCTION `dailyBase` → `base`, ICI ET NULLE PART AILLEURS.
 * Le fichier de réglages est plat (`dailyBase`, `dailyParJour`… pour ne pas
 * imbriquer un formulaire), le calcul veut un barème (`base`, `parJour`…). Si
 * chaque appelant faisait la conversion, il suffirait qu'un seul oublie une
 * clé pour qu'elle retombe silencieusement sur son défaut — et le montant
 * crédité ne serait plus celui que l'écran d'administration affiche.
 *
 * `dailyActif` n'en fait PAS partie : c'est une autorisation, pas un barème, et
 * elle se lit là où l'on décide (voir `economie-vivante.js`).
 */
export function dailyDe(reglages = {}) {
  const r = reglages && typeof reglages === 'object' ? reglages : {};
  return {
    base: entier(r.dailyBase, DEFAUTS.dailyBase, BORNES.dailyBase),
    parJour: entier(r.dailyParJour, DEFAUTS.dailyParJour, BORNES.dailyParJour),
    maxBonus: entier(r.dailyMaxBonus, DEFAUTS.dailyMaxBonus, BORNES.dailyMaxBonus),
    maxJours: entier(r.dailyMaxJours, DEFAUTS.dailyMaxJours, BORNES.dailyMaxJours),
  };
}

/**
 * Le prix d'UNE unité de carnet, résolu.
 *
 * ⚠️ UN SEUL ENDROIT OÙ LE `0` SENTINELLE SE RÉSOUT, ICI ET NULLE PART
 * AILLEURS — exactement pour la raison qui a fait écrire `dailyDe` : si chaque
 * appelant refaisait « `prixUnitaire` ou sinon le prix de la nature », il
 * suffirait qu'un seul oublie le mode `duree` pour qu'un carnet soit vendu au
 * prix de l'épisode et consommé à la tranche.
 *
 * L'ordre de lecture :
 *  1. un `prixUnitaire` explicite et strictement positif gagne, toujours ;
 *  2. sinon, en mode `duree`, une unité est une TRANCHE, donc `prixTranche` ;
 *  3. sinon, c'est le prix de la nature vendue.
 *
 * Rend toujours un entier ≥ 0, quoi qu'on lui donne : c'est un diviseur dans
 * `unitesPour` (§1.4), et un diviseur qui vaut `NaN` fabrique des unités
 * fantômes au lieu de lever.
 */
export function prixUnitaireDe(reglages = {}) {
  const r = reglages && typeof reglages === 'object' ? reglages : {};
  const explicite = entier(r.prixUnitaire, DEFAUTS.prixUnitaire, BORNES.prixUnitaire);
  if (explicite > 0) return explicite;
  const mode = MODES.includes(String(r.mode)) ? String(r.mode) : DEFAUTS.mode;
  if (mode === 'duree') return entier(r.prixTranche, DEFAUTS.prixTranche, BORNES.prixTranche);
  const nature = NATURES.includes(String(r.nature)) ? String(r.nature) : DEFAUTS.nature;
  return nature === 'film'
    ? entier(r.prixFilm, DEFAUTS.prixFilm, BORNES.prixFilm)
    : entier(r.prixEpisode, DEFAUTS.prixEpisode, BORNES.prixEpisode);
}

/**
 * Le plafond de SOLDE d'une personne, d'après son niveau.
 *
 * `min(plafondMax, plafond + plafondParNiveau × (niveau − 1))`, §5.3. C'est la
 * seule chose que le niveau donne, et c'est la seule que personne ne puisse
 * acheter.
 *
 * ⚠️ `plafondMax` NE PEUT JAMAIS FAIRE BAISSER `plafond`. Un exploitant qui
 * monte `plafond` à 10 000 sans penser à `plafondMax`, resté à son défaut de
 * 5 000, verrait sinon le plafond de tout le monde tomber de moitié au moment
 * où il croyait le doubler — et « maximum » aurait servi de minorant. On prend
 * donc `max(plafond, plafondMax)` comme borne dure.
 *
 * ⚠️ AVEC LES DÉFAUTS, IL REND `plafond`, AU POINT PRÈS. `plafondParNiveau`
 * vaut `0`, donc le terme du niveau est nul, donc le résultat est le plafond
 * d'aujourd'hui pour tout le monde : brancher cette fonction ne change aucun
 * comportement tant que personne n'a réglé la progression.
 */
export function plafondPersonnel(reglages = {}, niveau = 1) {
  const r = reglages && typeof reglages === 'object' ? reglages : {};
  const base = entier(r.plafond, DEFAUTS.plafond, BORNES.plafond);
  const parNiveau = entier(r.plafondParNiveau, DEFAUTS.plafondParNiveau, BORNES.plafondParNiveau);
  const dur = Math.max(base, entier(r.plafondMax, DEFAUTS.plafondMax, BORNES.plafondMax));
  // Le niveau 1 n'ajoute rien, et un niveau absent ou absurde vaut 1 : on ne
  // retire jamais de plafond à quelqu'un dont on n'a pas su lire le niveau.
  const n = Math.max(1, Math.floor(Number(niveau)) || 1);
  return Math.min(dur, base + parNiveau * (n - 1));
}

/**
 * LE BARÈME À GELER dans un carnet — les ONZE champs, et rien d'autre (§1.3).
 *
 * ⚠️ LE DÉFAUT QU'ON VIENT DE PAYER (C6) : ce barème gelait le TARIF et
 * oubliait les CONDITIONS DE VENTE. `economie.valeurDuCarnet` lit
 * `bareme.participants` / `bareme.boosters` (l. 456-459, « gelés avec la
 * vente ») ; `baremeDe` n'en produisait NI L'UN NI L'AUTRE, donc les `??` de
 * repli mordaient toujours et tout carnet se recalculait « une personne, aucun
 * boost ». Mesuré sur un carnet de 10 unités acheté seul et boosté, à 30
 * l'unité et −50 % de boost : la caisse annonçait 300 là où la vente avait
 * pris 150. Un facteur DEUX sur un chemin d'argent, sans un `NaN`, sans une
 * exception, sur un chiffre parfaitement plausible.
 *
 * Et ce n'est pas qu'un mauvais total : deux ventes aux conditions opposées
 * gelaient le même texte, donc la même empreinte, donc LA MÊME CLÉ DE CARNET
 * (§1.3, `(serveur, personne, empreinte)`). Le carnet acheté à moitié prix un
 * soir de boost et le carnet acheté plein tarif le lendemain fusionnaient en
 * silence dans un seul objet dont le `paye` ne valait plus rien.
 *
 * ⚠️ POURQUOI MAINTENANT, ALORS QUE §1.3 DIT QU'AJOUTER UN CHAMP CHANGE TOUTES
 * LES EMPREINTES. C'est vrai, et c'est exactement la raison : aucun carnet
 * n'existe encore en production (le carnet n'est pas livré, `carnetActif` est
 * éteint et `baremeDe` n'a aucun appelant). Changer la forme de l'empreinte est
 * gratuit aujourd'hui ; dans un mois, c'est une migration sur des carnets qui
 * portent de l'argent réel.
 *
 * ⚠️ POURQUOI GELER LE STATUT DE BOOSTER PLUTÔT QUE LE RELIRE (F6). Le statut
 * est volatile. Relu au présent, il achèterait rétroactivement du stock ancien :
 * acheter 40 unités hors boost, booster le serveur un soir, reprendre le carnet
 * au tarif du jour, encaisser −50 % sur les 40, puis cesser de booster. On gèle
 * donc TOUTES les conditions de vente, boosters ET participants — la part
 * boostée est un rapport (`boosters / participants`), geler son numérateur sans
 * son dénominateur ne gèlerait rien.
 *
 * ⚠️ L'ORDRE DES CLÉS EST LE CONTRAT. L'empreinte d'un carnet est un condensé
 * du `JSON.stringify` de cet objet : réordonner deux lignes ici change toutes
 * les empreintes du serveur d'un coup, et chaque carnet existant se retrouve
 * scellé sous une empreinte que plus rien ne calcule — donc invisible, donc
 * non reprenable, sans un message d'erreur. Ajouter un champ a le même effet.
 * Les deux champs neufs sont posés contre `remiseBoosterPourcent` parce qu'ils
 * en sont l'assiette : le taux et ce à quoi il s'applique se relisent ensemble,
 * et une remise de boost gelée sans sa part se repère alors à l'œil.
 *
 * ⚠️ C'EST UNE COPIE, JAMAIS UNE RÉFÉRENCE. Les paliers sont recopiés ligne à
 * ligne : geler le tableau rendu par `lirePoints` laisserait le carnet pointer
 * sur une grille que le prochain `PUT` réécrit, et le « barème gelé » suivrait
 * les changements qu'il est censé arrêter. C'est le défaut exact qui rendait
 * le regroupement incalculable dans la conception « Lot Scellé ».
 *
 * `prixUnitaire` y est RÉSOLU (jamais le `0` sentinelle) : un carnet doit
 * pouvoir se recalculer tout seul, des mois plus tard, sans relire les
 * réglages courants — c'est toute la raison d'être du gel.
 *
 * @param {object} reglages   ce que porte `access.json` — le TARIF
 * @param {object} conditions `{ participants, boosters }`, l'état du salon à
 *   l'instant de la vente — ce que `access.json` ne sait pas et ne saura
 *   jamais. Absent : une personne, aucun boost, c'est-à-dire exactement le
 *   repli qu'appliquaient déjà `prixDuLot` et `valeurDuCarnet`. Un achat sans
 *   conditions gèle donc les mêmes CHIFFRES qu'avant ce lot ; seule la FORME
 *   du barème change, et avec elle l'empreinte.
 */
export function baremeDe(reglages = {}, conditions = null) {
  const r = lirePoints(reglages);
  // ⚠️ ON ACCEPTE AUSSI LES CONDITIONS POSÉES SUR LE PREMIER ARGUMENT. La
  // façon naturelle d'appeler cette fonction quand on tient déjà un objet est
  // `baremeDe({ ...reglages, participants, boosters })` — et avec un second
  // paramètre seul, cet appel-là aurait rendu « une personne, aucun boost »
  // sans rien signaler, c'est-à-dire aurait REFAIT C6 à l'identique. Un oubli
  // qui coûte un facteur deux ne doit pas avoir de forme silencieuse.
  const brut = reglages && typeof reglages === 'object' ? reglages : {};
  const cond = conditions && typeof conditions === 'object' ? conditions : {};
  const participants = entier(cond.participants ?? brut.participants, 1, PARTICIPANTS_BORNES);
  // ⚠️ RABATTU SUR LE NOMBRE DE PRÉSENTS, comme `prixDuLot` le fait déjà
  // (l. 123 : `Math.min(boosters, participants)`). Sans ce rabat, `{2 présents,
  // 5 boosters}` et `{2 présents, 2 boosters}` décrivent la MÊME vente et
  // gèlent pourtant deux textes différents : deux empreintes, deux carnets, et
  // celui qu'on cherche n'est jamais celui qu'on trouve.
  const boosters = entier(cond.boosters ?? brut.boosters, 0, [0, participants]);
  return {
    mode: r.mode,
    prixEpisode: r.prixEpisode,
    prixFilm: r.prixFilm,
    prixTranche: r.prixTranche,
    minutesParTranche: r.minutesParTranche,
    remiseBoosterPourcent: r.remiseBoosterPourcent,
    participants,
    boosters,
    paliers: r.paliers.map((p) => ({ quantite: p.quantite, remisePourcent: p.remisePourcent })),
    nature: r.nature,
    prixUnitaire: prixUnitaireDe(r),
  };
}
